import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';

// Ancienne méthode (gardée en commentaire pour référence)
// const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GoogleCallback = () => {
  const navigate = useNavigate();
  const { checkSession } = useAuth();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('Processing Google authentication...');
  const hasProcessedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate processing using ref
    if (hasProcessedRef.current) return;

    const handleCallback = async () => {
      try {
        // Mark as processed immediately
        hasProcessedRef.current = true;
        
        // Get authorization code from URL
        const urlParams = new URLSearchParams(window.location.search);
        const code = urlParams.get('code');
        const errorParam = urlParams.get('error');

        if (errorParam) {
          setError('Authentication cancelled or failed');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        if (!code) {
          setError('No authorization code received');
          setTimeout(() => navigate('/'), 3000);
          return;
        }

        setStatus('Authenticating with Google...');

        // Send code to backend ONCE
        const response = await axios.post(
          `${API_BASE_URL}/api/auth/google/callback`,
          { code },
          { withCredentials: true }
        );

        if (response.data.message === 'Login successful') {
          setStatus('Login successful! Setting up your session...');
          
          // CRITICAL: Wait for cookie to be set in browser
          await new Promise(resolve => setTimeout(resolve, 800));
          
          // Force refresh the auth state
          try {
            await checkSession();
          } catch (e) {
            console.log('checkSession error (expected during transition):', e);
          }
          
          // Small delay to ensure AuthContext is updated
          await new Promise(resolve => setTimeout(resolve, 500));
          
          setStatus('Redirecting to dashboard...');
          
          // Navigate with replace to prevent back button issues
          navigate('/dashboard', { replace: true });
        }
      } catch (err) {
        console.error('Google OAuth error:', err);
        const errorDetail = err.response?.data?.detail || 'Failed to authenticate with Google';
        setError(errorDetail);
        
        // Clear the URL to prevent retry with same code
        window.history.replaceState({}, document.title, window.location.pathname);
        
        setTimeout(() => navigate('/'), 3000);
      }
    };

    handleCallback();
  }, []);  // Empty dependencies - run only once on mount

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-white">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full text-center">
        {error ? (
          <div>
            <div className="text-red-500 text-4xl mb-4">✕</div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Authentication Failed</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to login page...</p>
          </div>
        ) : (
          <div>
            <Loader2 className="h-12 w-12 animate-spin text-[#1C3E53] mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              {status}
            </h2>
            <p className="text-gray-600">Please wait while we complete your authentication...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GoogleCallback;
